import { Field, Item } from '@sitecore-content-sdk/nextjs';

/**
 * The GUID of the shared "Choose one" answer item. When Sitecore includes this
 * option in a question dropdown it should be pre-selected and treated as a valid
 * selection (no placeholder, no required error).
 */
export const CHOOSE_ONE_QUESTION_ANSWER_ID = '76f71dd2-6f3a-4a38-b23b-89e6a3f55c7c';

/**
 * A normalized dropdown option consumed by the client components.
 */
export type DropdownOption = {
  label: string;
  value: string;
};

/**
 * Sitecore answer items expose the label/value under different field names
 * depending on the template ("answer"/"answerId" or
 * "businessIndustries"/"businessIndustriesId"). This type covers both.
 */
type QuestionAnswerItem = Item & {
  fields?: {
    answer?: Field<string>;
    answerId?: Field<string>;
    businessIndustries?: Field<string>;
    businessIndustriesId?: Field<string>;
  };
};

const getFieldValue = (field: Field<string> | undefined): string | undefined =>
  typeof field?.value === 'string' && field.value ? field.value : undefined;

/**
 * Maps a single Sitecore answer item to a normalized dropdown option.
 * Returns null when the item has no usable label.
 */
export const toQuestionOption = (item: Item | undefined): DropdownOption | null => {
  if (!item) {
    return null;
  }

  const questionItem = item as QuestionAnswerItem;
  const label =
    getFieldValue(questionItem.fields?.answer) ||
    getFieldValue(questionItem.fields?.businessIndustries) ||
    questionItem.displayName ||
    questionItem.name;

  if (!label) {
    return null;
  }

  return {
    label,
    value:
      getFieldValue(questionItem.fields?.answerId) ||
      getFieldValue(questionItem.fields?.businessIndustriesId) ||
      questionItem.id ||
      label,
  };
};

/**
 * Maps a list of Sitecore answer items to normalized dropdown options,
 * dropping any items without a usable label.
 */
export const getQuestionOptions = (source: Item[] | undefined): DropdownOption[] =>
  (source || []).map(toQuestionOption).filter((option): option is DropdownOption => !!option);

/**
 * Returns the "Choose one" value when the given options include it, otherwise
 * undefined. Components use this to pre-select "Choose one" and hide the
 * placeholder when the option is available.
 */
export const getDefaultChooseOneValue = (
  options: DropdownOption[] | undefined
): string | undefined =>
  options?.some((option) => option.value === CHOOSE_ONE_QUESTION_ANSWER_ID)
    ? CHOOSE_ONE_QUESTION_ANSWER_ID
    : undefined;

/**
 * Determines whether a dropdown value should be treated as "no selection" for
 * validation purposes. The shared "Choose one" option is only a placeholder
 * substitute when the field is required — in that case it is treated the same
 * as the empty placeholder. When the field is optional, "Choose one" is a valid
 * selection and passes through like any other answer.
 */
export const isUnselectedQuestionValue = (
  value: string | undefined,
  required: boolean
): boolean => {
  if (!value?.trim()) {
    return true;
  }

  return required && value === CHOOSE_ONE_QUESTION_ANSWER_ID;
};
